<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Collecting this information helps your financial professional to
          fulfill regulatory obligations.
        </p>
      </div>
    </help-drawer>
    <v-form name="Business" class="form" data-test-id="business-line-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.BUSINESS.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="business-header">
              <span class="headerlight">
                What's your <strong>line of business?</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your line of business...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isBusinessComplete && formHidden && !isSyncing"
          :submitted="isBusinessComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-column class="justify-center full col-12 col-sm-4" v-if="!formHidden && !isSyncing">
          <div class="faux-label required" data-test-id="business-line-label">Select a Line of Business</div>
            <div>
                <v-select
                  v-model="business"
                  :items="businessOptions"
                  :backgroundColor="inputBackgroundColor"
                  ref="businessList"
                  outlined
                  aria-required="true"
                  placeholder="Choose Business Line"
                  dense
                  hide-details="auto"
                  @change="$v.business.$touch()"
                  :error-messages="businessErrors"
                  data-test-id="business-line-select"
                >
                </v-select>
            </div>
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
        </v-column>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="business-line-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required } from 'vuelidate/lib/validators';
import { MENUS, BUSINESS_LINE_LIST } from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'Business',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    business: null,
    MENUS: MENUS,
  }),

  validations() {
    return {
      business: {
        required,
      },
      validationGroup: ['business'],
    };
  },

  computed: {
    // Is business complete
    isBusinessComplete() {
      return this.isPageComplete(MENUS.CLIENT.BUSINESS.id);
    },
    
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.BUSINESS.id);
      return page && page.status === 'IN_PROGRESS';
    },
    /* Dynamic errors */
    businessErrors() {
      const errors = [];
      if (!this.$v.business.$dirty) return errors;
      !this.$v.business.required &&
        errors.push('A selection is required');
      return errors;
    },
    helpTitle() {
      return 'Why do you need this?';
    },
  },

  methods: {
    /* Nav to Next Page in flow */
    next(route = null) {      
      let to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        business: this.business,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {        
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isBusinessComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isBusinessComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    this.interviewPages.addEmploymentPages();
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.BUSINESS.id);
    // Initialize form data for current interview
    this.formHidden = this.isBusinessComplete;

    this.businessOptions = [];
    Object.keys(BUSINESS_LINE_LIST).map((key) => {
      const val = BUSINESS_LINE_LIST[key];    
      this.businessOptions.push(val);
    });
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // If business is complete, hide the form
      if (this.isBusinessComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>
<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
